import {
  birdStatusLabel,
  birdStatusTone,
  describeAge,
  shortRingNumber,
} from "@breenova/domain";
import {
  Button,
  buttonVariants,
  Card,
  CardInset,
  FilterChip,
  FilterChipRow,
  SegmentedControl,
  SexIcon,
  StatusPill,
  cn,
} from "@breenova/ui";
import {
  Bird as BirdIcon,
  ChevronLeft,
  ChevronRight,
  Download,
  LayoutGrid,
  List,
  Plus,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import {
  PageHeader,
  RailGroup,
  RailPrimaryAction,
  RailRow,
  RailSection,
} from "@/components/app-shell";
import {
  useBird,
  useBirds,
  useReferenceData,
  type BirdFilters,
  type BirdSummary,
} from "@/lib/birds";
import { downloadExport } from "@/lib/exports";
import { SPLIT_VIEW, useMediaQuery } from "@/lib/media";
import { useOverview } from "@/lib/stats";
import { BirdDetailPage } from "@/routes/birds/bird-detail-page";

type ViewMode = "grid" | "list";

const VIEW_OPTIONS = [
  { value: "list" as const, label: "List view", icon: <List className="size-4" /> },
  { value: "grid" as const, label: "Grid view", icon: <LayoutGrid className="size-4" /> },
];

/** Remembered across visits: a breeder who prefers the grid prefers it every time. */
const VIEW_STORAGE_KEY = "breenova.birds.view";

function readStoredView(): ViewMode {
  try {
    return globalThis.localStorage?.getItem(VIEW_STORAGE_KEY) === "grid" ? "grid" : "list";
  } catch {
    // Private mode throws rather than returning null. The default is fine.
    return "list";
  }
}

/**
 * The whole register as a spreadsheet.
 *
 * Desktop only, and deliberately: the file lands in a downloads folder, which is a place
 * a phone has but nobody opens. It exports everything rather than the current filter —
 * that is what the endpoint does, and a button that silently exported 12 of 70 birds
 * because a chip was set would be worse than one that always exports 70.
 */
function ExportCsvButton() {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={busy}
      className="hidden md:inline-flex"
      onClick={() => {
        setBusy(true);
        setFailed(false);

        void downloadExport("/api/v1/exports/birds.csv", "breenova-birds.csv")
          .catch(() => setFailed(true))
          .finally(() => setBusy(false));
      }}
    >
      <Download className="size-4" aria-hidden />
      {failed ? "Export failed" : busy ? "Exporting…" : "Export CSV"}
    </Button>
  );
}

/** The three cuts the rail offers. `undefined` is "everything". */
const STATUS_FILTERS: readonly { label: string; status: BirdFilters["status"] }[] = [
  { label: "In the aviary", status: "InAviary" },
  { label: "Quarantine", status: "Quarantined" },
  { label: "Sold or gone", status: "Sold" },
];

export function BirdListPage() {
  /*
   * Today's search box hands over here with `?search=`. Read once, as the initial state
   * rather than as a controlled value: from this point the field is the breeder's, and a
   * URL that kept overwriting it would fight whatever they typed next.
   */
  const [params] = useSearchParams();
  const handedOver = params.get("search") ?? "";

  const [filters, setFilters] = useState<BirdFilters>(
    handedOver ? { page: 1, search: handedOver } : { page: 1 },
  );
  const [search, setSearch] = useState(handedOver);
  const [view, setView] = useState<ViewMode>(readStoredView);
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Which row the peek panel is showing.
   *
   * Desktop only, and it is why the table's rows are buttons rather than links: at this
   * width opening a bird should not cost the breeder the list they found it in. Below
   * `lg` there is no room for a panel beside the list, so the cards stay links and go to
   * the full record.
   */
  const [peekId, setPeekId] = useState<string | undefined>(undefined);

  const reference = useReferenceData();
  const birds = useBirds(filters);

  function update(next: Partial<BirdFilters>): void {
    // Any filter change resets to page one — staying on page four of a narrower result
    // set shows an empty list and looks like a failure.
    setFilters((current) => ({ ...current, ...next, page: 1 }));
    setPeekId(undefined);
  }

  function changeView(next: ViewMode): void {
    setView(next);
    try {
      globalThis.localStorage?.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Not worth surfacing; the choice simply will not persist.
    }
  }

  const species = reference.data?.species ?? [];

  // The design's chip row: species first, then the two cuts a breeder makes day to day.
  // Anything rarer lives behind the filter button.
  const speciesChips = useMemo(
    () => species.filter((s) => s.code === "canary" || s.code === "european-goldfinch"),
    [species],
  );

  const items = birds.data?.items ?? [];
  const hasFilters = Boolean(
    filters.speciesId || filters.sex || filters.status || filters.search || filters.hatchYear,
  );
  const stats = useOverview().data;
  const thisYear = new Date().getFullYear();
  const split = useMediaQuery(SPLIT_VIEW);
  const selected = peekId ?? items[0]?.id;

  const column = (
    <>
        <RailPrimaryAction>
          <Link
            to="/birds/new"
            className={cn(
              "flex h-12 w-full items-center justify-center gap-2 rounded-control",
              "bg-action text-cta-sm text-on-action",
              "transition-opacity hover:opacity-90",
            )}
          >
            <Plus className="size-4.5" aria-hidden />
            Add a bird
          </Link>
        </RailPrimaryAction>

        {/*
          The filters move into the rail on a desktop, where they are always visible.
          The chip row below is the phone's version of the same state — one `filters`
          object drives both, so a cut made in one place is the cut the other shows.
        */}
        <RailSection>
          <RailGroup label="Filter">
            {STATUS_FILTERS.map((option) => (
              <RailRow
                key={option.label}
                label={option.label}
                selected={filters.status === option.status}
                onClick={() =>
                  update({ status: filters.status === option.status ? undefined : option.status })
                }
              />
            ))}
          </RailGroup>

          <RailGroup label="Sex">
            <div className="flex flex-wrap gap-field px-md">
              <FilterChip
                active={filters.sex === "Male"}
                onClick={() => update({ sex: filters.sex === "Male" ? undefined : "Male" })}
              >
                Cocks
              </FilterChip>
              <FilterChip
                active={filters.sex === "Female"}
                onClick={() => update({ sex: filters.sex === "Female" ? undefined : "Female" })}
              >
                Hens
              </FilterChip>
            </div>
          </RailGroup>

          {species.length > 0 ? (
            <RailGroup label="Species">
              {/* Chips rather than rows: a species is a narrow word, and four of them wrap
                  into two lines where four full-width rows would take four. */}
              <div className="flex flex-wrap gap-field px-md">
                {species.map((option) => (
                  <FilterChip
                    key={option.id}
                    active={filters.speciesId === option.id}
                    onClick={() =>
                      update({
                        speciesId: filters.speciesId === option.id ? undefined : option.id,
                      })
                    }
                  >
                    {option.name}
                  </FilterChip>
                ))}
              </div>
            </RailGroup>
          ) : null}
        </RailSection>

        <PageHeader
          compact={split}
          title="Birds"
          subtitle={stats ? `${stats.birdsInAviary} in the aviary` : undefined}
          action={
            /* Phone only: it opens the status chips, and from `md` those are rail rows
               that are already on screen. */
            <button
              type="button"
              aria-label="More filters"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((open) => !open)}
              className={cn(
                "flex size-11.5 shrink-0 items-center justify-center rounded-full xl:hidden",
                "shadow-card transition-colors",
                filtersOpen
                  ? "bg-primary-fixed text-on-primary-fixed"
                  : "bg-surface-container-lowest text-on-surface",
              )}
            >
              <SlidersHorizontal className="size-4.75" />
            </button>
          }
          toolbar={
            <>
              {/* Full width on a phone, where it is the whole second line of the header;
                  a fixed column on a desktop, where it shares that line with the title and
                  a stretched search box would read as an address bar. */}
              <div className={cn("relative flex-1", split ? "" : "md:w-80 md:flex-none")}>
                <Search
                  className="pointer-events-none absolute left-lg top-1/2 size-4.5 -translate-y-1/2 text-on-surface-variant"
                  aria-hidden
                />
                {/* Not the shared Input: this one is a search field on the page ground, and
                    it carries the icon's inset rather than the standard field padding. */}
                <input
                  type="search"
                  placeholder="Ring number, cage, mutation"
                  aria-label="Search birds"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      update({ search });
                    }
                  }}
                  onBlur={() => update({ search })}
                  className={cn(
                    "h-12.5 w-full rounded-control bg-surface-container-lowest shadow-card md:h-12",
                    "pl-[calc(var(--spacing-lg)+var(--spacing)*7)] pr-lg",
                    "text-body text-on-surface placeholder:text-on-surface-variant",
                    "outline-none transition-shadow",
                    "focus:shadow-[inset_0_0_0_2.75px_var(--color-action)]",
                  )}
                />
              </div>

              {/* The card row carries the photo on a phone, so the grid view is redundant
                  there. It earns its place from md up, where a grid fits four across — but
                  not in the split view's column, where the record beside it is the photo. */}
              {split ? null : (
              <>
              <SegmentedControl
                label="View"
                value={view}
                options={VIEW_OPTIONS}
                onChange={changeView}
                className="hidden md:inline-flex"
              />

              {/* Not "New bird": the rail carries that from `md`, and this is the slot the
                  design gives to the thing a breeder does with a whole filtered register
                  rather than with one row of it. */}
              <ExportCsvButton />
              </>
              )}
            </>
          }
        />

        {/* Phone only: the rail is where these live from `md`. */}
        <div className="flex flex-col gap-4 px-5 pb-4 xl:hidden">
          <FilterChipRow aria-label="Filter birds">
            <FilterChip
              active={!hasFilters}
              count={stats?.totalBirds}
              onClick={() => setFilters({ page: 1, sort: filters.sort })}
            >
              All
            </FilterChip>

            <FilterChip
              active={filters.sex === "Male"}
              count={stats?.males}
              onClick={() => update({ sex: filters.sex === "Male" ? undefined : "Male" })}
            >
              Cocks
            </FilterChip>

            <FilterChip
              active={filters.sex === "Female"}
              count={stats?.females}
              onClick={() => update({ sex: filters.sex === "Female" ? undefined : "Female" })}
            >
              Hens
            </FilterChip>

            <FilterChip
              active={filters.status === "Quarantined"}
              onClick={() =>
                update({ status: filters.status === "Quarantined" ? undefined : "Quarantined" })
              }
            >
              Quarantine
            </FilterChip>

            {/* This year's hatch: the birds still too young to pair. */}
            <FilterChip
              active={filters.hatchYear === thisYear}
              onClick={() =>
                update({ hatchYear: filters.hatchYear === thisYear ? undefined : thisYear })
              }
            >
              Young
            </FilterChip>
          </FilterChipRow>

          {filtersOpen ? (
            <FilterChipRow aria-label="More filters">
              {speciesChips.map((s) => (
                <FilterChip
                  key={s.id}
                  active={filters.speciesId === s.id}
                  onClick={() => update({ speciesId: filters.speciesId === s.id ? undefined : s.id })}
                >
                  {s.name}
                </FilterChip>
              ))}
              <FilterChip
                active={filters.status === "InAviary"}
                onClick={() =>
                  update({ status: filters.status === "InAviary" ? undefined : "InAviary" })
                }
              >
                In aviary
              </FilterChip>
              <FilterChip
                active={filters.status === "Sold"}
                onClick={() => update({ status: filters.status === "Sold" ? undefined : "Sold" })}
              >
                Sold
              </FilterChip>
              <FilterChip
                active={filters.status === "Deceased"}
                onClick={() =>
                  update({ status: filters.status === "Deceased" ? undefined : "Deceased" })
                }
              >
                Deceased
              </FilterChip>
            </FilterChipRow>
          ) : null}
        </div>

        <section className={cn("px-5", split ? "pb-lg" : "md:px-gutter-wide")} aria-live="polite">
          {birds.isPending ? (
            <p className="py-xl text-center text-body text-on-surface-variant">Loading birds…</p>
          ) : birds.isError ? (
            <Card className="flex flex-col items-start gap-sm p-lg">
              <p className="text-body text-error">Could not load your birds.</p>
              <Button variant="secondary" size="sm" onClick={() => void birds.refetch()}>
                Try again
              </Button>
            </Card>
          ) : items.length === 0 ? (
            <EmptyState hasFilters={hasFilters} />
          ) : (
            /*
             * Cards up to `lg`, and from there a table beside a peek panel.
             *
             * A card row is the right shape for a register read one-handed: a photo, a ring
             * number, and the two facts that fit. On a monitor the same card is a seventh of
             * a row of empty space, and a table puts every fact a breeder compares birds on
             * — sex, age, cage, status — into a column they can run an eye down.
             *
             * The grid view is exempt. It is chosen when the breeder wants photographs, and
             * a table is the opposite of that request.
             */
            split ? (
              <BirdList birds={items} selectedId={selected} onSelect={setPeekId} />
            ) : (
            <>
              <div className={cn(view === "grid" ? "" : "xl:hidden")}>
                {view === "grid" ? <BirdGrid birds={items} /> : <BirdList birds={items} />}
              </div>

              {view === "grid" ? null : (
                <div className="hidden items-start gap-lg xl:grid xl:grid-cols-[minmax(0,1fr)_var(--container-peek)]">
                  <BirdTable
                    birds={items}
                    peekId={peekId}
                    onPeek={setPeekId}
                    page={birds.data?.page ?? 1}
                    totalPages={birds.data?.totalPages ?? 1}
                    totalCount={birds.data?.totalCount ?? items.length}
                    onPage={(page) => setFilters((current) => ({ ...current, page }))}
                  />

                  <BirdPeek id={peekId ?? items[0]?.id} />
                </div>
              )}
            </>
            )
          )}
        </section>
    </>
  );

  /*
   * A tablet in landscape: the list in a 390px column, the selected bird beside it. The
   * column scrolls with the page and the record stays put, so running down the list does
   * not lose the bird already open.
   */
  if (split) {
    return (
      <div className="flex min-h-dvh items-start">
        <div className="flex w-97.5 shrink-0 flex-col self-stretch border-r border-outline-variant">
          {column}
        </div>
        <div className="sticky top-0 h-dvh min-w-0 flex-1 overflow-y-auto">
          {selected ? <BirdDetailPage id={selected} embedded /> : null}
        </div>
      </div>
    );
  }

  return <div className="flex flex-col md:gap-lg">{column}</div>;
}

/**
 * The register as a table, from `lg` up.
 *
 * A real `<table>` rather than a grid of divs: this is tabular data, and the semantics
 * are what let a screen reader announce "Cage, BR-I01" instead of reading six unlabelled
 * cells. The row is a button, not a link — it fills the panel beside it rather than
 * navigating, and the panel carries the link to the full record.
 */
function BirdTable({
  birds,
  peekId,
  onPeek,
  page,
  totalPages,
  totalCount,
  onPage,
}: {
  birds: readonly BirdSummary[];
  peekId: string | undefined;
  onPeek: (id: string) => void;
  page: number;
  totalPages: number;
  totalCount: number;
  onPage: (page: number) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const selected = peekId ?? birds[0]?.id;

  return (
    <Card className="flex min-w-0 flex-col px-xl pb-lg pt-xs">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="text-label-caps uppercase text-on-surface-variant">
            <th scope="col" className="h-touch w-44 pr-md font-inherit">
              Ring
            </th>
            <th scope="col" className="h-touch pr-md font-inherit">
              Species &amp; mutation
            </th>
            <th scope="col" className="h-touch w-12 pr-md font-inherit">
              Sex
            </th>
            <th scope="col" className="h-touch w-24 pr-md font-inherit">
              Age
            </th>
            <th scope="col" className="h-touch w-20 pr-md font-inherit">
              Cage
            </th>
            <th scope="col" className="h-touch w-28 font-inherit">
              Status
            </th>
          </tr>
        </thead>

        <tbody>
          {birds.map((bird) => (
            <tr
              key={bird.id}
              onClick={() => onPeek(bird.id)}
              aria-selected={bird.id === selected}
              className={cn(
                "cursor-pointer border-t border-outline-variant transition-colors",
                bird.id === selected ? "bg-surface" : "hover:bg-surface-container-low",
              )}
            >
              <td className="py-sm pr-md">
                <span className="flex items-center gap-sm">
                  <Thumbnail bird={bird} className="size-8.5 shrink-0" />
                  {/* The button carries the row's accessible name and the keyboard
                      focus; the whole row is the mouse target. */}
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onPeek(bird.id);
                    }}
                    className="truncate text-data text-on-surface outline-none focus-visible:underline"
                    data-numeric
                  >
                    {bird.ringNumber ? shortRingNumber(bird.ringNumber) : "Unringed"}
                  </button>
                </span>
              </td>

              <td className="max-w-0 truncate py-sm pr-md text-label text-on-surface">
                {bird.speciesName}
                {bird.mutations.length > 0 ? ` · ${bird.mutations.join(", ")}` : ""}
              </td>

              <td className="py-sm pr-md">
                <SexGlyph sex={bird.sex} />
              </td>

              <td className="py-sm pr-md text-caption text-on-surface-variant" data-numeric>
                {ageLabel(bird.hatchDate, today)}
              </td>

              <td className="py-sm pr-md text-micro text-on-surface-variant" data-numeric>
                {bird.locationCode ?? "—"}
              </td>

              <td className="py-sm">
                <StatusPill size="sm" tone={birdStatusTone(bird.status)}>
                  {birdStatusLabel(bird.status)}
                </StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between gap-md pt-md">
        <span className="text-micro font-normal text-on-surface-variant" data-numeric>
          Showing {birds.length} of {totalCount}
        </span>

        {totalPages > 1 ? (
          <Pagination page={page} totalPages={totalPages} onPage={onPage} />
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Page buttons, capped at five.
 *
 * A register of 3,000 birds is sixty pages, and sixty discs is not navigation. Five
 * around the current page plus the two arrows is what fits under the table without
 * wrapping, and a breeder looking for one bird searches rather than pages to it.
 */
function Pagination({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
}) {
  const first = Math.max(1, Math.min(page - 2, totalPages - 4));
  const shown = Array.from({ length: Math.min(5, totalPages) }, (_, index) => first + index).filter(
    (value) => value <= totalPages,
  );

  const step = (target: number, label: string, icon: ReactNode) => (
    <button
      type="button"
      aria-label={label}
      disabled={target < 1 || target > totalPages}
      onClick={() => onPage(target)}
      className={cn(
        "flex size-8 items-center justify-center rounded-full transition-colors",
        "bg-surface-container text-on-surface-variant hover:bg-surface-container-high",
        "disabled:opacity-45 disabled:hover:bg-surface-container",
      )}
    >
      {icon}
    </button>
  );

  return (
    <nav aria-label="Pages" className="flex items-center gap-field">
      {step(page - 1, "Previous page", <ChevronLeft className="size-4" />)}

      {shown.map((value) => (
        <button
          key={value}
          type="button"
          aria-current={value === page ? "page" : undefined}
          onClick={() => onPage(value)}
          className={cn(
            "flex size-8 items-center justify-center rounded-full text-micro transition-colors",
            value === page
              ? "bg-secondary-container text-on-secondary-container"
              : "text-on-surface-variant hover:bg-surface-container-low",
          )}
          data-numeric
        >
          {value}
        </button>
      ))}

      {step(page + 1, "Next page", <ChevronRight className="size-4" />)}
    </nav>
  );
}

/**
 * The selected bird, beside the register rather than instead of it.
 *
 * It shows what a breeder checks before deciding they have the right bird — who its
 * parents were, how inbred it is, what was last written down — and then gets out of the
 * way with a link to the record proper. Anything that needs a second query beyond the
 * bird itself stays off it: this panel changes on every row click, and a click that
 * fires three requests is a list that stutters as you arrow down it.
 */
function BirdPeek({ id }: { id: string | undefined }) {
  const bird = useBird(id);
  const today = new Date().toISOString().slice(0, 10);

  if (!id) {
    return null;
  }

  if (bird.isPending || !bird.data) {
    return (
      <Card className="flex flex-col gap-md p-lg">
        <p className="text-body text-on-surface-variant">
          {bird.isError ? "Could not load that bird." : "Loading…"}
        </p>
      </Card>
    );
  }

  const record = bird.data;

  return (
    <Card className="flex flex-col overflow-hidden">
      <Thumbnail
        bird={{
          id: record.id,
          speciesName: record.speciesName,
          thumbnailUrl:
            (record.photos.find((photo) => photo.isPrimary) ?? record.photos[0])?.thumbnailUrl,
        }}
        className="aspect-[4/3] w-full rounded-none"
      />

      <div className="flex flex-col gap-md p-lg">
        <div className="flex flex-col gap-hair">
          <span className="text-label-caps uppercase text-on-surface-variant">
            {record.speciesName}
            {record.mutations.length > 0
              ? ` · ${record.mutations.map((mutation) => mutation.name).join(", ")}`
              : ""}
          </span>
          <span className="text-section-header text-on-surface" data-numeric>
            {record.ringNumber ?? "Unringed"}
          </span>
        </div>

        <CardInset className="flex justify-between gap-sm" data-numeric>
          <PeekFact label="Sex" value={record.sex} />
          <PeekFact label="Age" value={ageLabel(record.hatchDate, today)} />
          <PeekFact label="Cage" value={record.locationCode ?? "—"} />
        </CardInset>

        {record.sireRingNumber || record.damRingNumber ? (
          <div className="flex flex-col gap-sm">
            <span className="text-label-caps uppercase text-on-surface-variant">Parents</span>
            <div className="flex gap-sm">
              <PeekParent sex="Male" ring={record.sireRingNumber} id={record.sireId} />
              <PeekParent sex="Female" ring={record.damRingNumber} id={record.damId} />
            </div>
          </div>
        ) : null}

        <Link
          to={`/birds/${record.id}`}
          className={cn(buttonVariants({ size: "sm" }), "mt-xs w-full")}
        >
          Open record
        </Link>
      </div>
    </Card>
  );
}

function PeekFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-px">
      <span className="text-tile-label uppercase text-on-surface-variant">{label}</span>
      <span className="truncate text-data text-on-surface">{value}</span>
    </div>
  );
}

function PeekParent({
  sex,
  ring,
  id,
}: {
  sex: BirdSummary["sex"];
  ring: string | null;
  id: string | null;
}) {
  const body = (
    <>
      <span
        aria-hidden
        className={cn(
          "flex size-7.5 shrink-0 items-center justify-center rounded-full",
          sex === "Male"
            ? "bg-primary-fixed text-on-primary-fixed-variant"
            : "bg-tertiary-fixed text-on-tertiary-fixed-variant",
        )}
      >
        <SexIcon sex={sex} className="size-3.5" />
      </span>
      <span className="truncate text-micro text-on-surface" data-numeric>
        {ring ? shortRingNumber(ring) : "Unknown"}
      </span>
    </>
  );

  const className = "flex flex-1 items-center gap-sm rounded-sm bg-surface p-sm";

  return id ? (
    <Link to={`/birds/${id}`} className={cn(className, "transition-colors hover:bg-surface-container")}>
      {body}
    </Link>
  ) : (
    <span className={className}>{body}</span>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <Card className="flex flex-col items-center gap-sm p-xl text-center">
      <p className="text-card-title text-on-surface">
        {hasFilters ? "No birds match those filters" : "No birds yet"}
      </p>
      <p className="text-body text-on-surface-variant">
        {hasFilters
          ? "Clear a filter to widen the search."
          : "Add your first bird to start the register."}
      </p>
      {hasFilters ? null : (
        <Link to="/birds/new" className={cn(buttonVariants({ size: "sm" }), "mt-sm")}>
          <Plus className="size-4" />
          Add a bird
        </Link>
      )}
    </Card>
  );
}

/**
 * Each bird is its own card rather than a row in a divided list.
 *
 * A divider says "these are lines of one table"; a card says "this is one animal's
 * record", which is what a breeder is actually scrolling through. It also gives the
 * round thumbnail somewhere to sit without the list looking punched full of holes.
 */
function BirdList({
  birds,
  selectedId,
  onSelect,
}: {
  birds: readonly BirdSummary[];
  /**
   * Set in the split view, where a row selects into the pane beside the list rather than
   * navigating away from it. The row then drops its status tag — the record beside it
   * carries that — and the selected one is ringed.
   */
  selectedId?: string | undefined;
  onSelect?: ((id: string) => void) | undefined;
}) {
  if (onSelect) {
    return (
      <ul className="flex flex-col gap-2">
        {birds.map((bird) => {
          const selected = bird.id === selectedId;

          return (
            <li key={bird.id}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onSelect(bird.id)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-card py-2.25 pl-2.25 pr-3.5 text-left",
                  "transition-colors",
                  selected
                    ? "bg-primary-fixed shadow-[inset_0_0_0_2px_var(--color-accent-mark-soft)]"
                    : "bg-surface-container-lowest shadow-card hover:bg-surface-container-low",
                )}
              >
                <Thumbnail bird={bird} className="size-12 shrink-0" />

                <span className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className="flex items-center gap-1.5 truncate text-label text-on-surface" data-numeric>
                    {bird.ringNumber ? shortRingNumber(bird.ringNumber) : "Unringed"}
                    <SexGlyph sex={bird.sex} />
                  </span>
                  <span className="truncate text-caption text-on-surface-variant">
                    {bird.speciesName}
                    {bird.mutations.length > 0 ? ` · ${bird.mutations.join(", ")}` : ""}
                  </span>
                </span>

                <span className="shrink-0 text-micro font-semibold text-on-surface-variant" data-numeric>
                  {bird.locationCode ?? "—"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    // Two columns from `xl`. A row is about 76px tall and reads at any width, so a single
    // column on a 1400px window is not a longer list, it is the same list with two thirds
    // of the screen left blank and twice as much scrolling.
    <ul className="grid grid-cols-1 gap-2 xl:grid-cols-2">
      {birds.map((bird) => (
        <li key={bird.id}>
          <Link to={`/birds/${bird.id}`} className="block">
            <Card
              shape="row"
              className={cn(
                // The design pads a row asymmetrically: tight around the round
                // thumbnail on the left, roomier on the right where the text ends.
                "flex items-center gap-3 py-2.5 pl-2.5 pr-4",
                "transition-colors hover:bg-surface-container-low",
              )}
            >
              <Thumbnail bird={bird} className="size-13 shrink-0" />

              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p
                  className="flex items-center gap-1.5 truncate text-row text-on-surface"
                  data-numeric
                >
                  {bird.ringNumber ? shortRingNumber(bird.ringNumber) : "Unringed"}
                  <SexGlyph sex={bird.sex} />
                </p>
                <p className="truncate text-caption text-on-surface-variant">
                  {bird.speciesName}
                  {bird.mutations.length > 0 ? ` · ${bird.mutations.join(", ")}` : ""}
                </p>
              </div>

              <div className="flex shrink-0 flex-col items-end gap-1">
                <StatusPill size="sm" tone={birdStatusTone(bird.status)}>
                  {birdStatusLabel(bird.status)}
                </StatusPill>
                <p className="text-micro font-semibold text-on-surface-variant" data-numeric>
                  {bird.locationCode ?? "—"}
                </p>
              </div>
            </Card>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** The desktop grid. Photo first, then identity, then age and cage. */
function BirdGrid({ birds }: { birds: readonly BirdSummary[] }) {
  const today = new Date().toISOString().slice(0, 10);

  return (
    <ul className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {birds.map((bird) => (
        <li key={bird.id}>
          <Link to={`/birds/${bird.id}`} className="block h-full">
            <Card shape="row" className="flex h-full flex-col overflow-hidden">
              <div className="relative">
                <Thumbnail bird={bird} className="aspect-[4/3] w-full rounded-none" />

                <StatusPill
                  size="sm"
                  tone={birdStatusTone(bird.status)}
                  className="absolute right-sm top-sm"
                >
                  {birdStatusLabel(bird.status)}
                </StatusPill>
              </div>

              <div className="flex flex-1 flex-col gap-sm p-lg">
                <div className="flex items-center justify-between gap-sm">
                  <span className="truncate text-card-title text-on-surface" data-numeric>
                    {bird.ringNumber ? shortRingNumber(bird.ringNumber) : "Unringed"}
                  </span>
                  <SexGlyph sex={bird.sex} />
                </div>

                <p className="truncate text-caption text-on-surface-variant">
                  {bird.speciesName}
                  {bird.mutations.length > 0 ? ` · ${bird.mutations.join(", ")}` : ""}
                </p>

                <dl className="mt-auto flex items-end justify-between gap-sm">
                  <div className="flex min-w-0 flex-col gap-hair">
                    <dt className="text-tile-label uppercase text-on-surface-variant">Age</dt>
                    <dd className="truncate text-data text-on-surface" data-numeric>
                      {ageLabel(bird.hatchDate, today)}
                    </dd>
                  </div>

                  <div className="flex min-w-0 flex-col items-end gap-hair">
                    <dt className="text-tile-label uppercase text-on-surface-variant">Cage</dt>
                    <dd className="truncate text-data text-on-surface">
                      {bird.locationCode ?? "—"}
                    </dd>
                  </div>
                </dl>
              </div>
            </Card>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Sex as the Mars and Venus signs, which is how a ringed bird is labelled on paper.
 * Written out for a screen reader, which cannot say a glyph.
 */
function SexGlyph({ sex }: { sex: BirdSummary["sex"] }) {
  if (sex === "Unknown") {
    return (
      <span className="shrink-0 text-label text-on-surface-variant">
        ?<span className="sr-only">Sex unknown</span>
      </span>
    );
  }

  const male = sex === "Male";

  return (
    <span className={cn("shrink-0", male ? "text-primary-strong" : "text-secondary-strong")}>
      <SexIcon sex={sex} className="size-3.75" />
      <span className="sr-only">{sex}</span>
    </span>
  );
}

/**
 * The bird's photo, or a bird mark on a tinted disc when there is none. A blank tile in a list
 * reads as a loading failure; a mark reads as "no photo yet".
 */
function Thumbnail({
  bird,
  className,
}: {
  /* The fields it reads, rather than a whole `BirdSummary` — the peek panel draws
     the same tile from a `BirdDetail`, which carries its photographs as a list. */
  bird: { id?: string | undefined; thumbnailUrl?: string | null | undefined; speciesName: string };
  className?: string;
}) {
  if (bird.thumbnailUrl) {
    return (
      <img
        src={bird.thumbnailUrl}
        alt=""
        loading="lazy"
        className={cn("rounded-full object-cover", className)}
      />
    );
  }

  return (
    <div
      aria-hidden
      className={cn(
        "flex items-center justify-center rounded-full",
        THUMBNAIL_TONES[toneIndex(bird.id ?? bird.speciesName)],
        className,
      )}
    >
      <BirdIcon className="size-5/12" />
    </div>
  );
}

/** Age string for a hatch date, or a dash. Exported for the detail screen to reuse. */
export function ageLabel(hatchDate: string | null | undefined, today: string): string {
  return hatchDate ? describeAge(hatchDate, today) : "—";
}

/**
 * The three grounds a photo-less bird gets — sage, terracotta and sand — picked from its
 * id so the same bird always wears the same one. A column of identical discs reads as one
 * placeholder repeated; three that alternate read as a list of animals.
 */
const THUMBNAIL_TONES = [
  "bg-primary-fixed text-on-primary-fixed-variant",
  "bg-secondary-fixed text-on-secondary-fixed-variant",
  "bg-surface-container-low text-on-surface-body",
] as const;

function toneIndex(key: string): 0 | 1 | 2 {
  let hash = 0;

  for (const char of key) {
    hash = (hash * 31 + char.charCodeAt(0)) % 997;
  }

  return (hash % 3) as 0 | 1 | 2;
}
